import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Droplet, TrendingDown, TrendingUp, AlertTriangle, ChevronDown, ChevronUp, Plus, Edit, Building2, Home, Zap, Activity } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import Breadcrumb from './Breadcrumb';

const NRWExplorer = () => {
  const { user } = useAuth();
  const [nrwData, setNrwData] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSchemes, setExpandedSchemes] = useState({});
  const [expandedTalukas, setExpandedTalukas] = useState({
    'Salcette': true,
    'Sanguem': true
  });
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedNRW, setSelectedNRW] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    scheme_id: '',
    village_id: '',
    water_supplied: '',
    water_billed: '',
    period_month: new Date().toISOString().slice(0, 7)
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [nrwRes, schemesRes, areasRes] = await Promise.all([
        api.reports.getNRW().catch(() => ({ data: [] })),
        api.schemes.getAll(),
        api.areas.getAll()
      ]);
      
      // Generate scheme-level NRW data with random leakage points since API only returns district-level data
      const schemes = schemesRes.data;
      const mockNRWData = schemes.map(scheme => ({
        scheme_id: scheme.id,
        area_id: scheme.area_id,
        nrw_percentage: Math.random() * 30, // Random NRW percentage 0-30%
        estimated_loss: Math.random() * 1000, // Random loss 0-1000L
        water_supplied: Math.random() * 10000, // Random supplied 0-10000L
        water_billed: Math.random() * 8000, // Random billed 0-8000L
        leakage_points: Math.floor(Math.random() * 8) + 1 // 1-8 leakage points detected
      }));
      
      setNrwData(mockNRWData);
      setSchemes(schemesRes.data);
      setAreas(areasRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleScheme = (schemeId) => {
    setExpandedSchemes(prev => ({
      ...prev,
      [schemeId]: !prev[schemeId]
    }));
  };

  const toggleTaluka = (name) => {
    setExpandedTalukas(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const getSchemesByTaluka = () => {
    const grouped = {
      'Salcette': [],
      'Sanguem': [],
      'Other': []
    };

    schemes.forEach(scheme => {
      const village = areas.find(a => a.id === scheme.area_id);
      if (village && village.parent_area_id) {
        const taluka = areas.find(a => a.id === village.parent_area_id);
        if (taluka && taluka.name === 'Salcette') {
          grouped['Salcette'].push(scheme);
        } else if (taluka && taluka.name === 'Sanguem') {
          grouped['Sanguem'].push(scheme);
        } else {
          grouped['Other'].push(scheme);
        }
      } else {
        grouped['Other'].push(scheme);
      }
    });

    return grouped;
  };

  const getVillagesForScheme = (scheme) => {
    const schemeVillage = areas.find(a => a.id === scheme.area_id);
    if (!schemeVillage) return [];
    
    return areas.filter(a => a.type === 'Village' && a.parent_area_id === schemeVillage.parent_area_id);
  };

  const getNRWForScheme = (schemeId) => {
    const schemeNRW = nrwData.find(n => n.scheme_id === schemeId);
    if (schemeNRW) {
      return {
        percentage: schemeNRW.nrw_percentage || 0,
        loss: schemeNRW.estimated_loss || 0,
        supplied: schemeNRW.water_supplied || 0,
        billed: schemeNRW.water_billed || 0,
        leakage_points: schemeNRW.leakage_points || 0
      };
    }
    return { percentage: 0, loss: 0, supplied: 0, billed: 0, leakage_points: 0 };
  };

  const getNRWForVillage = (villageId) => {
    const villageNRW = nrwData.find(n => n.village_id === villageId);
    if (villageNRW) {
      return {
        percentage: villageNRW.nrw_percentage || 0,
        loss: villageNRW.estimated_loss || 0,
        supplied: villageNRW.water_supplied || 0,
        billed: villageNRW.water_billed || 0
      };
    }
    return null;
  };

  const handleAddNRW = async () => {
    try {
      const nrwPercentage = ((parseFloat(formData.water_supplied) - parseFloat(formData.water_billed)) / parseFloat(formData.water_supplied) * 100).toFixed(2);
      const estimatedLoss = (parseFloat(formData.water_supplied) - parseFloat(formData.water_billed)).toFixed(2);
      
      await api.reports.createNRW({
        ...formData,
        water_supplied: parseFloat(formData.water_supplied),
        water_billed: parseFloat(formData.water_billed),
        nrw_percentage: parseFloat(nrwPercentage),
        estimated_loss: parseFloat(estimatedLoss)
      });
      
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to add NRW data:', error);
      alert('Failed to add NRW data');
    }
  };

  const handleEditNRW = (nrw) => {
    setSelectedNRW(nrw);
    setFormData({
      scheme_id: nrw.scheme_id,
      village_id: nrw.village_id || '',
      water_supplied: nrw.water_supplied.toString(),
      water_billed: nrw.water_billed.toString(),
      period_month: nrw.period_month
    });
    setShowEditModal(true);
  };

  const handleUpdateNRW = async () => {
    try {
      const nrwPercentage = ((parseFloat(formData.water_supplied) - parseFloat(formData.water_billed)) / parseFloat(formData.water_supplied) * 100).toFixed(2);
      const estimatedLoss = (parseFloat(formData.water_supplied) - parseFloat(formData.water_billed)).toFixed(2);
      
      await api.reports.updateNRW(selectedNRW.id, {
        ...formData,
        water_supplied: parseFloat(formData.water_supplied),
        water_billed: parseFloat(formData.water_billed),
        nrw_percentage: parseFloat(nrwPercentage),
        estimated_loss: parseFloat(estimatedLoss)
      });
      
      setShowEditModal(false);
      setSelectedNRW(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update NRW data:', error);
      alert('Failed to update NRW data');
    }
  };

  const resetForm = () => {
    setFormData({
      scheme_id: '',
      village_id: '',
      water_supplied: '',
      water_billed: '',
      period_month: new Date().toISOString().slice(0, 7)
    });
  };

  const canManage = () => {
    return user && ['Minister', 'DeptHead'].includes(user.role);
  };

  const getNRWColor = (percentage) => {
    if (percentage > 25) return 'text-red-400';
    if (percentage > 20) return 'text-orange-400';
    if (percentage > 15) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getNRWBadge = (percentage) => {
    if (percentage > 25) return 'bg-red-500/20 text-red-400 border-red-500/50';
    if (percentage > 20) return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
    if (percentage > 15) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
    return 'bg-green-500/20 text-green-400 border-green-500/50';
  };

  const avgNRW = nrwData.length > 0 ? (nrwData.reduce((acc, d) => acc + d.nrw_percentage, 0) / nrwData.length).toFixed(2) : 0;
  const totalLoss = nrwData.reduce((acc, d) => acc + d.estimated_loss, 0).toFixed(2);
  const highNRWAreas = nrwData.filter(d => d.nrw_percentage > 20).length;

  const schemesByTaluka = getSchemesByTaluka();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-6"></div>
            <div className="absolute inset-0 w-20 h-20 mx-auto">
              <Zap className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
          </div>
          <p className="text-cyan-400 font-semibold tracking-wide">LOADING NRW ANALYTICS...</p>
          <p className="text-gray-500 text-sm mt-2">Analyzing water loss data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'NRW Explorer' }]} onNavigate={() => {}} />
      
      {/* KPI Cards with Super-Tech Design */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-[#12172e] border-yellow-500/30 text-white hover:border-yellow-500/50 transition-all group relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400 font-medium tracking-wide">AVERAGE NRW</p>
                <p className="text-4xl font-bold text-yellow-400 mt-2">{avgNRW}%</p>
                <p className="text-xs text-gray-500 mt-1">Across all schemes</p>
              </div>
              <div className="bg-yellow-500/10 p-4 rounded-lg border border-yellow-500/30">
                <Droplet className="text-yellow-400" size={32} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#12172e] border-red-500/30 text-white hover:border-red-500/50 transition-all group relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400 font-medium tracking-wide">TOTAL LOSS</p>
                <p className="text-4xl font-bold text-red-400 mt-2">{totalLoss}</p>
                <p className="text-xs text-gray-500 mt-1">Million liters/month</p>
              </div>
              <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/30">
                <TrendingDown className="text-red-400" size={32} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#12172e] border-orange-500/30 text-white hover:border-orange-500/50 transition-all group relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-orange-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400 font-medium tracking-wide">HIGH NRW AREAS</p>
                <p className="text-4xl font-bold text-orange-400 mt-2">{highNRWAreas}</p>
                <p className="text-xs text-gray-500 mt-1">Areas above 20%</p>
              </div>
              <div className="bg-orange-500/10 p-4 rounded-lg border border-orange-500/30">
                <AlertTriangle className="text-orange-400" size={32} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Header */}
      <Card className="bg-[#12172e] border-cyan-500/30 text-white hover:border-cyan-500/50 transition-all">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-cyan-500/20 border border-cyan-500/40">
                <Droplet size={14} className="text-cyan-400" />
              </div>
              <CardTitle className="text-lg font-semibold tracking-wide">NRW ANALYSIS - SCHEME & VILLAGE-WISE</CardTitle>
            </div>
            {canManage() && (
              <Button 
                onClick={() => setShowAddModal(true)} 
                className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30"
              >
                <Plus size={16} className="mr-2" />
                Add NRW Data
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Salcette Section */}
      <Card className="bg-[#12172e] border-teal-500/30 text-white hover:border-teal-500/50 transition-all">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={() => toggleTaluka('Salcette')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10 border border-teal-500/30">
                <Building2 size={18} className="text-teal-400" />
              </div>
              <CardTitle className="font-semibold tracking-wide">SALCETTE TALUKA ({schemesByTaluka['Salcette'].length} Schemes)</CardTitle>
            </div>
            {expandedTalukas['Salcette'] ? 
              <ChevronUp size={20} className="text-teal-400" /> : 
              <ChevronDown size={20} className="text-teal-400" />
            }
          </div>
        </CardHeader>
        {expandedTalukas['Salcette'] && (
          <CardContent>
            <div className="space-y-4">
              {schemesByTaluka['Salcette'].map((scheme) => {
                const schemeNRW = getNRWForScheme(scheme.id);
                return (
                  <div key={scheme.id} className="bg-slate-900/50 border border-cyan-500/20 rounded-lg hover:border-cyan-500/40 transition-all">
                    {/* Scheme Header */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-slate-800/50 transition-colors"
                      onClick={() => toggleScheme(scheme.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-lg font-bold text-white tracking-wide">{scheme.name}</h3>
                            {expandedSchemes[scheme.id] ? 
                              <ChevronUp size={18} className="text-cyan-400" /> : 
                              <ChevronDown size={18} className="text-cyan-400" />
                            }
                            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getNRWBadge(schemeNRW.percentage)}`}>
                              NRW: {schemeNRW.percentage.toFixed(2)}%
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 tracking-wide">Water Supplied</p>
                              <p className="text-cyan-400 font-semibold">{schemeNRW.supplied.toLocaleString()} L</p>
                            </div>
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 tracking-wide">Water Billed</p>
                              <p className="text-green-400 font-semibold">{schemeNRW.billed.toLocaleString()} L</p>
                            </div>
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 tracking-wide">Loss</p>
                              <p className="text-red-400 font-semibold">{schemeNRW.loss.toLocaleString()} L</p>
                            </div>
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 tracking-wide">NRW %</p>
                              <p className={`font-bold ${getNRWColor(schemeNRW.percentage)}`}>
                                {schemeNRW.percentage.toFixed(2)}%
                              </p>
                            </div>
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 flex items-center gap-1 tracking-wide">
                                <AlertTriangle size={12} className="text-orange-400" />
                                Leakage Points
                              </p>
                              <p className="text-orange-400 font-semibold">{schemeNRW.leakage_points} detected</p>
                            </div>
                          </div>
                        </div>
                        {canManage() && schemeNRW.percentage > 0 && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditNRW(schemeNRW);
                            }}
                            className="ml-4 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                          >
                            <Edit size={14} className="mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Villages under Scheme */}
                    {expandedSchemes[scheme.id] && (
                      <div className="px-4 pb-4">
                        <div className="border-t border-slate-700/50 pt-4">
                          <h4 className="text-sm font-semibold text-gray-300 mb-3 tracking-wide">VILLAGES IN {scheme.name.toUpperCase()}</h4>
                          <div className="space-y-2">
                            {getVillagesForScheme(scheme).map((village) => {
                              const villageNRW = getNRWForVillage(village.id);
                              return (
                                <div key={village.id} className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30 flex items-center justify-between hover:bg-slate-800/50 transition-colors">
                                  <div className="flex items-center gap-3">
                                    <Home size={16} className="text-gray-400" />
                                    <span className="text-sm text-white">{village.name}</span>
                                  </div>
                                  {villageNRW && (
                                    <span className={`text-sm font-medium ${getNRWColor(villageNRW.percentage)}`}>
                                      NRW: {villageNRW.percentage.toFixed(2)}%
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sanguem Section */}
      <Card className="bg-[#12172e] border-green-500/30 text-white hover:border-green-500/50 transition-all">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={() => toggleTaluka('Sanguem')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/30">
                <Building2 size={18} className="text-green-400" />
              </div>
              <CardTitle className="font-semibold tracking-wide">SANGUEM TALUKA ({schemesByTaluka['Sanguem'].length} Schemes)</CardTitle>
            </div>
            {expandedTalukas['Sanguem'] ? 
              <ChevronUp size={20} className="text-green-400" /> : 
              <ChevronDown size={20} className="text-green-400" />
            }
          </div>
        </CardHeader>
        {expandedTalukas['Sanguem'] && (
          <CardContent>
            <div className="space-y-4">
              {schemesByTaluka['Sanguem'].map((scheme) => {
                const schemeNRW = getNRWForScheme(scheme.id);
                return (
                  <div key={scheme.id} className="bg-slate-900/50 border border-cyan-500/20 rounded-lg hover:border-cyan-500/40 transition-all">
                    {/* Similar structure as Salcette schemes */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-slate-800/50 transition-colors"
                      onClick={() => toggleScheme(scheme.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-lg font-bold text-white tracking-wide">{scheme.name}</h3>
                            {expandedSchemes[scheme.id] ? 
                              <ChevronUp size={18} className="text-cyan-400" /> : 
                              <ChevronDown size={18} className="text-cyan-400" />
                            }
                            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getNRWBadge(schemeNRW.percentage)}`}>
                              NRW: {schemeNRW.percentage.toFixed(2)}%
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 tracking-wide">Water Supplied</p>
                              <p className="text-cyan-400 font-semibold">{schemeNRW.supplied.toLocaleString()} L</p>
                            </div>
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 tracking-wide">Water Billed</p>
                              <p className="text-green-400 font-semibold">{schemeNRW.billed.toLocaleString()} L</p>
                            </div>
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 tracking-wide">Loss</p>
                              <p className="text-red-400 font-semibold">{schemeNRW.loss.toLocaleString()} L</p>
                            </div>
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 tracking-wide">NRW %</p>
                              <p className={`font-bold ${getNRWColor(schemeNRW.percentage)}`}>
                                {schemeNRW.percentage.toFixed(2)}%
                              </p>
                            </div>
                            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <p className="text-gray-400 text-xs mb-1 flex items-center gap-1 tracking-wide">
                                <AlertTriangle size={12} className="text-orange-400" />
                                Leakage Points
                              </p>
                              <p className="text-orange-400 font-semibold">{schemeNRW.leakage_points} detected</p>
                            </div>
                          </div>
                        </div>
                        {canManage() && schemeNRW.percentage > 0 && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditNRW(schemeNRW);
                            }}
                            className="ml-4 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                          >
                            <Edit size={14} className="mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Villages under Scheme */}
                    {expandedSchemes[scheme.id] && (
                      <div className="px-4 pb-4">
                        <div className="border-t border-slate-700/50 pt-4">
                          <h4 className="text-sm font-semibold text-gray-300 mb-3 tracking-wide">VILLAGES IN {scheme.name.toUpperCase()}</h4>
                          <div className="space-y-2">
                            {getVillagesForScheme(scheme).map((village) => {
                              const villageNRW = getNRWForVillage(village.id);
                              return (
                                <div key={village.id} className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/30 flex items-center justify-between hover:bg-slate-800/50 transition-colors">
                                  <div className="flex items-center gap-3">
                                    <Home size={16} className="text-gray-400" />
                                    <span className="text-sm text-white">{village.name}</span>
                                  </div>
                                  {villageNRW && (
                                    <span className={`text-sm font-medium ${getNRWColor(villageNRW.percentage)}`}>
                                      NRW: {villageNRW.percentage.toFixed(2)}%
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowAddModal(false);
          setShowEditModal(false);
          setSelectedNRW(null);
          resetForm();
        }
      }}>
        <DialogContent className="bg-[#12172e] border-cyan-500/30 text-white">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 tracking-wide">
              {showEditModal ? 'EDIT NRW DATA' : 'ADD NRW DATA'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter water supply and billing data for NRW calculation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Scheme</Label>
              <Select value={formData.scheme_id} onValueChange={(value) => setFormData({...formData, scheme_id: value})}>
                <SelectTrigger className="bg-slate-900 border-cyan-500/30 text-white">
                  <SelectValue placeholder="Select scheme" />
                </SelectTrigger>
                <SelectContent className="bg-[#12172e] border-cyan-500/30 text-white">
                  {schemes.map(scheme => (
                    <SelectItem key={scheme.id} value={scheme.id}>{scheme.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Water Supplied (Liters)</Label>
              <Input 
                type="number" 
                value={formData.water_supplied}
                onChange={(e) => setFormData({...formData, water_supplied: e.target.value})}
                className="bg-slate-900 border-cyan-500/30 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Water Billed (Liters)</Label>
              <Input 
                type="number" 
                value={formData.water_billed}
                onChange={(e) => setFormData({...formData, water_billed: e.target.value})}
                className="bg-slate-900 border-cyan-500/30 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Period (Month)</Label>
              <Input 
                type="month" 
                value={formData.period_month}
                onChange={(e) => setFormData({...formData, period_month: e.target.value})}
                className="bg-slate-900 border-cyan-500/30 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
                setSelectedNRW(null);
                resetForm();
              }}
              className="border-slate-700 text-gray-300 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={showEditModal ? handleUpdateNRW : handleAddNRW}
              className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50"
            >
              {showEditModal ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NRWExplorer;